// eslint configured: rule not present in CRA build
import { useEffect, useState } from "react";
import { apiGet, apiMutate, STAGES, PLATFORMS } from "@/lib/api";
import { Plus, Kanban, ChevronRight } from "lucide-react";
import IdeaModal from "@/components/IdeaModal";
import EmptyState from "@/components/EmptyState";
import { track, EVENTS } from "@/lib/analytics";
import { markCoachStep } from "@/lib/coach";

const SEAL = "var(--seal)";
const INK = "var(--ink)";
const INK_55 = "var(--ink-55)";
const INK_72 = "var(--ink-72)";

function PipelineCard({ idea, onClick, onDragStart, onAdvance }) {
  const platformsList = idea.platforms && idea.platforms.length ? idea.platforms : [idea.platform];
  const firstPlatform = PLATFORMS.find((p) => p.id === platformsList[0]);
  const extraCount = platformsList.length - 1;
  const curIdx = STAGES.findIndex((s) => s.id === idea.stage);
  const nextStage = curIdx >= 0 && curIdx < STAGES.length - 1 ? STAGES[curIdx + 1] : null;
  return (
    <div
      data-testid={`pipeline-card-${idea.id}`}
      draggable
      onDragStart={(e) => onDragStart(e, idea)}
      onClick={() => onClick(idea)}
      className="rounded-lg p-3 cursor-grab transition-all hover:-translate-y-0.5 relative group"
      style={{ background: "var(--surface)", border: "1px solid var(--ink-22)" }}
    >
      <div className="text-sm leading-tight pr-7" style={{ color: INK, fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 24, 'wght' 450" }}>
        {idea.title}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 mt-2.5">
        <span className="tag">
          {firstPlatform?.short || platformsList[0]}
          {extraCount > 0 && <span style={{ color: "var(--seal)" }}> +{extraCount}</span>}
        </span>
        {(idea.tags || []).slice(0, 2).map((t) => (
          <span key={t} className="tag" style={{ background: "transparent", color: INK_55 }}>#{t}</span>
        ))}
      </div>
      {nextStage && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onAdvance(idea, nextStage.id); }}
          className="absolute top-2 right-2 p-1.5 rounded-full transition opacity-100 md:opacity-0 md:group-hover:opacity-100"
          style={{ background: "var(--bg)", border: "1px solid var(--ink-22)", color: "var(--seal)" }}
          title={`Move to ${nextStage.label}`}
          data-testid={`pipeline-card-${idea.id}-advance`}
        >
          <ChevronRight size={14} />
        </button>
      )}
    </div>
  );
}

export default function Pipeline() {
  const [ideas, setIdeas] = useState([]);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = async () => setIdeas(await apiGet("/ideas"));
  useEffect(() => { load(); track(EVENTS.PIPELINE_VIEWED, {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = filter === "all" ? ideas : ideas.filter((i) => i.platform === filter);

  const onDragStart = (e, idea) => {
    e.dataTransfer.setData("text/plain", idea.id);
    e.dataTransfer.effectAllowed = "move";
  };

  const onDrop = async (e, stage) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData("text/plain");
    const idea = ideas.find((i) => i.id === id);
    if (!idea || idea.stage === stage) return;
    setIdeas((cur) => cur.map((i) => (i.id === id ? { ...i, stage } : i)));
    await apiMutate("patch", `/ideas/${id}`, { stage });
    markCoachStep("move-pipeline");
    load();
  };

  // Touch-friendly stage advance (used by chevron button on each card).
  const advanceStage = async (idea, nextStageId) => {
    if (!idea || idea.stage === nextStageId) return;
    setIdeas((cur) => cur.map((i) => (i.id === idea.id ? { ...i, stage: nextStageId } : i)));
    await apiMutate("patch", `/ideas/${idea.id}`, { stage: nextStageId });
    try { track(EVENTS.IDEA_STAGE_CHANGED, { from: idea.stage, to: nextStageId, via: "chevron" }); } catch { /* noop */ }
    markCoachStep("move-pipeline");
    load();
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
        <div>
          <div className="eyebrow"><span className="seal-dot mr-2 align-middle" /> ii. The Pipeline</div>
          <h1 className="font-heading mt-4 text-4xl md:text-5xl" style={{ fontVariationSettings: "'opsz' 96, 'wght' 450" }}>
            Six rooms. <span className="font-display-italic">One</span> craft.
          </h1>
        </div>
        <button className="btn-primary" onClick={() => setShowNew(true)} data-testid="pipeline-new-btn">
          <Plus size={14} /> Add idea
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-6" data-testid="pipeline-filters">
        {[{ id: "all", short: "All" }, ...PLATFORMS].map((p) => (
          <button
            key={p.id}
            onClick={() => setFilter(p.id)}
            data-testid={`pipeline-filter-${p.id}`}
            className="px-3.5 py-1.5 rounded-full text-xs transition"
            style={{
              background: filter === p.id ? "var(--ink)" : "transparent",
              color: filter === p.id ? "var(--bg)" : INK_55,
              border: filter === p.id ? "1px solid var(--ink)" : "1px solid var(--ink-22)",
            }}
          >
            {p.short}
          </button>
        ))}
      </div>

      {ideas.length === 0 ? (
        <EmptyState
          testid="pipeline-empty"
          icon={Kanban}
          eyebrow="The Pipeline"
          title="Six rooms,"
          titleItalic="ready."
          body="Every idea moves through Idea → Research → Script → Create → Schedule → Published. Drop your first card to begin."
          primaryLabel="Add your first idea"
          onPrimary={() => setShowNew(true)}
        />
      ) : (
      <div className="flex overflow-x-auto gap-3 pb-6 snap-x snap-mandatory md:snap-none" data-testid="pipeline-board" data-coach="pipeline-board">
        {STAGES.map((s, i) => {
          const cards = filtered.filter((c) => c.stage === s.id);
          return (
            <div
              key={s.id}
              onDragOver={(e) => { e.preventDefault(); setDragOver(s.id); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={(e) => onDrop(e, s.id)}
              className="w-[80vw] sm:w-72 flex-shrink-0 snap-center flex flex-col gap-2 p-3 rounded-xl transition"
              style={{
                background: dragOver === s.id ? "rgba(212,160,90,0.06)" : "var(--surface)",
                border: dragOver === s.id ? "1px solid var(--seal)" : "1px solid var(--ink-22)",
              }}
              data-testid={`pipeline-column-${s.id}`}
            >
              <div className="flex items-center justify-between mb-2 px-1">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] uppercase tracking-[0.22em]" style={{ color: INK_55 }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-heading text-base" style={{ fontVariationSettings: "'opsz' 32, 'wght' 500" }}>
                    {s.label}
                  </span>
                </div>
                <span className="text-[10px]" style={{ color: INK_55 }}>{cards.length}</span>
              </div>
              {cards.map((c) => (
                <PipelineCard key={c.id} idea={c} onClick={setActive} onDragStart={onDragStart} onAdvance={advanceStage} />
              ))}
              {cards.length === 0 && (
                <div className="text-xs py-6 text-center rounded-md" style={{ color: "var(--ink-38)", border: "1px dashed var(--ink-22)" }}>
                  Drop here
                </div>
              )}
            </div>
          );
        })}
      </div>
      )}

      {active && <IdeaModal idea={active} onClose={() => setActive(null)} onSaved={() => { setActive(null); load(); }} onDeleted={() => { setActive(null); load(); }} />}
      {showNew && <IdeaModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}
